// Minesweeper.jsx (or .js)
import React from "react";
import "./Minesweeper.css"; // We'll include the CSS below too

class Minesweeper extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      height: 9,
      width: 9,
      mines: 10,
      grid: [],
      minesLeft: 10,
      gameStatus: "playing", // "playing" | "won" | "lost"
      face: "😊", // Smiley face states
    };
  }

  componentDidMount() {
    this.restartGame();
  }

  restartGame = () => {
    this.setState({
      grid: this.createBoard(this.state.height, this.state.width, this.state.mines),
      minesLeft: this.state.mines,
      gameStatus: "playing",
      face: "😊",
    });
  };

  createBoard = (height, width, mines, firstClick = null) => {
    const totalCells = height * width;
    let minePositions = [];

    // First click safety: don't place mine where user clicked
    const excluded = firstClick !== null ? [firstClick.y * width + firstClick.x] : [];

    const positions = Array.from({ length: totalCells }, (_, i) => i).filter(
      (pos) => !excluded.includes(pos)
    );

    while (minePositions.length < mines) {
      const idx = Math.floor(Math.random() * positions.length);
      minePositions.push(positions.splice(idx, 1)[0]);
    }

    const grid = [];
    for (let y = 0; y < height; y++) {
      const row = [];
      for (let x = 0; x < width; x++) {
        const isMine = minePositions.includes(y * width + x);
        row.push({
          x,
          y,
          isMine,
          isRevealed: false,
          isFlagged: false,
          neighborMines: 0,
        });
      }
      grid.push(row);
    }

    // Calculate neighbor mine counts
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (!grid[y][x].isMine) {
          grid[y][x].neighborMines = this.countNeighborMines(grid, x, y);
        }
      }
    }

    return grid;
  };

  countNeighborMines = (grid, x, y) => {
    let count = 0;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const ny = y + dy;
        const nx = x + dx;
        if (ny >= 0 && ny < grid.length && nx >= 0 && nx < grid[0].length) {
          if (grid[ny][nx].isMine) count++;
        }
      }
    }
    return count;
  };

  handleCellClick = (x, y) => {
    if (this.state.gameStatus !== "playing") return;

    let { grid, minesLeft } = this.state;
    const cell = grid[y][x];

    if (cell.isRevealed || cell.isFlagged) return;

    // First click: regenerate board if needed
    if (this.countRevealed() === 0 && cell.isMine) {
      grid = this.createBoard(this.state.height, this.state.width, this.state.mines, { x, y });
      this.setState({ grid, face: "😮" });
      setTimeout(() => this.setState({ face: "😊" }), 200);
    }

    if (cell.isMine) {
      this.revealAllMines();
      this.setState({ gameStatus: "lost", face: "💀" });
      return;
    }

    this.revealCell(grid, x, y);

    if (cell.neighborMines === 0) {
      this.floodReveal(grid, x, y);
    }

    this.setState({ grid, face: "😊" }, () => {
      if (this.checkWin()) {
        this.setState({ gameStatus: "won", face: "😎", minesLeft: 0 });
      }
    });
  };

  handleRightClick = (e, x, y) => {
    e.preventDefault();
    if (this.state.gameStatus !== "playing") return;

    const { grid, minesLeft } = this.state;
    const cell = grid[y][x];

    if (cell.isRevealed) return;

    cell.isFlagged = !cell.isFlagged;
    this.setState({
      grid,
      minesLeft: cell.isFlagged ? minesLeft - 1 : minesLeft + 1,
      face: "😊"
    });
  };

  revealCell = (grid, x, y) => {
    if (!grid[y] || !grid[y][x]) return;
    const cell = grid[y][x];
    if (cell.isRevealed || cell.isFlagged) return;
    cell.isRevealed = true;
  };

  floodReveal = (grid, x, y) => {
    const queue = [{ x, y }];
    const visited = new Set();

    while (queue.length > 0) {
      const { x, y } = queue.shift();
      const key = `${x},${y}`;
      if (visited.has(key)) continue;
      visited.add(key);

      this.revealCell(grid, x, y);

      if (grid[y][x].neighborMines === 0) {
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = x + dx;
            const ny = y + dy;
            if (nx >= 0 && nx < grid[0].length && ny >= 0 && ny < grid.length) {
              if (!grid[ny][nx].isRevealed && !grid[ny][nx].isFlagged) {
                queue.push({ x: nx, y: ny });
              }
            }
          }
        }
      }
    }
  };

  revealAllMines = () => {
    const grid = this.state.grid.map(row =>
      row.map(cell => ({
        ...cell,
        isRevealed: cell.isMine ? true : cell.isRevealed,
        isFlagged: cell.isMine ? false : cell.isFlagged,
      }))
    );
    this.setState({ grid });
  };

  countRevealed = () => {
    return this.state.grid.flat().filter(c => c.isRevealed).length;
  };

  checkWin = () => {
    const totalSafe = this.state.height * this.state.width - this.state.mines;
    return this.countRevealed() >= totalSafe;
  };

  handleSizeChange = (field, value) => {
    const num = Math.max(5, Math.min(30, parseInt(value) || 9));
    this.setState({ [field]: num }, () => {
      if (field === "height" || field === "width") {
        const maxMines = Math.floor((this.state.height * this.state.width) * 0.3);
        if (this.state.mines > maxMines) {
          this.setState({ mines: maxMines });
        }
      }
      this.restartGame();
    });
  };

  render() {
    const { height, width, mines, minesLeft, gameStatus, face, grid } = this.state;

    const numberColors = {
      1: "#0000ff",
      2: "#008000",
      3: "#ff0000",
      4: "#000080",
      5: "#800000",
      6: "#008080",
      7: "#000000",
      8: "#808080",
    };

    return (
      <div className="minesweeper">
        <div className="minesweeper-header">
          <div className="counter">
            <span>💣 {minesLeft.toString().padStart(3, "0")}</span>
          </div>
          <button className="face" onClick={this.restartGame}>
            {face}
          </button>
          <div className="counter">
            <span>⏱️ 000</span>
          </div>
        </div>

        <div className="game-info">
          <label>
            Height:
            <input
              type="number"
              min="5"
              max="30"
              value={height}
              onChange={(e) => this.handleSizeChange("height", e.target.value)}
            />
          </label>
          <label>
            Width:
            <input
              type="number"
              min="5"
              max="30"
              value={width}
              onChange={(e) => this.handleSizeChange("width", e.target.value)}
            />
          </label>
          <label>
            Mines:
            <input
              type="number"
              min="1"
              max={Math.floor((height * width) * 0.3)}
              value={mines}
              onChange={(e) => this.setState({ mines: Math.max(1, parseInt(e.target.value) || 10) }, this.restartGame)}
            />
          </label>
        </div>

        <div
          className="board"
          style={{
            gridTemplateColumns: `repeat(${width}, 30px)`,
            width: `${width * 32}px`,
          }}
        >
          {grid.map((row, y) =>
            row.map((cell, x) => (
              <div
                key={`${x}-${y}`}
                className={`cell ${
                  cell.isRevealed
                    ? cell.isMine
                      ? "mine"
                      : cell.neighborMines === 0
                      ? "empty"
                      : "number"
                    : "hidden"
                } ${cell.isFlagged ? "flag" : ""}`}
                onClick={() => this.handleCellClick(x, y)}
                onContextMenu={(e) => this.handleRightClick(e, x, y)}
              >
                {cell.isRevealed && cell.isMine && "💣"}
                {cell.isRevealed && !cell.isMine && cell.neighborMines > 0 && (
                  <span style={{ color: numberColors[cell.neighborMines] }}>
                    {cell.neighborMines}
                  </span>
                )}
                {cell.isFlagged && "🚩"}
              </div>
            ))
          )}
        </div>

        {gameStatus !== "playing" && (
          <div className="game-over">
            <h2>{gameStatus === "won" ? "🎉 You Won! 🎉" : "💥 Game Over 💥"}</h2>
            <button onClick={this.restartGame}>Play Again</button>
          </div>
        )}
      </div>
    );
  }
}

export default Minesweeper;